import {
  AvroOptional,
  log as logRawClickEvent,
  RawClickPlatform,
  RawClickSurface,
} from "@indeed/signals-schema/com/indeed/signals/schema/viewjob/RawClickEvent";
import { Platform } from "react-native";

import { getTareBridge } from "../../../../../../shared/native-modules/tare/getTareBridge";

export type RawClickOptions = {
  surface?: string;
  ia?: boolean;
  isFirstAutoLoad?: boolean;
  isApplyStartClick?: boolean;
  isCanary?: boolean;
  dgToken?: string;
  viewJobDisplay?: string;
  ctk?: string | null;
  accountId?: string | number | null;
  grps?: string | null;
};

export type RawClickAttrs = {
  tk?: string;
  jobKey?: string;
};

export function resolveRawClickProctorGroups(
  hostProctorGroups: string | null | undefined,
): string | null | undefined {
  if (hostProctorGroups != null) {
    return hostProctorGroups;
  }

  try {
    return getTareBridge()?.getProctorLoggingStringSync?.();
  } catch {
    return undefined;
  }
}

function resolveRawClickSurface(surface: string | undefined): RawClickSurface {
  switch (surface) {
    case "homepage":
      return RawClickSurface.HOMEPAGE;
    case "serp":
      return RawClickSurface.SERP;
    case "viewjob":
      return RawClickSurface.VIEWJOB;
    default:
      return RawClickSurface.UNKNOWN;
  }
}

function resolveRawClickPlatform(viewJobDisplay: string | undefined): RawClickPlatform {
  if (Platform.OS === "ios") {
    return RawClickPlatform.IOS;
  }
  if (Platform.OS === "android") {
    return RawClickPlatform.ANDROID;
  }
  if (viewJobDisplay?.startsWith("DESKTOP")) {
    return RawClickPlatform.DESKTOP;
  }
  return RawClickPlatform.MOBILE_WEB;
}

export function emitRawClickSignal(
  attrs: RawClickAttrs,
  isLoggedIn: boolean,
  options: RawClickOptions = {},
): void {
  try {
    logRawClickEvent({
      tk: AvroOptional.string(attrs.tk),
      jobKey: AvroOptional.string(attrs.jobKey),
      surface: { RawClickSurface: resolveRawClickSurface(options.surface) },
      ia: AvroOptional.boolean(options.ia),
      isFirstAutoLoad: AvroOptional.boolean(options.isFirstAutoLoad),
      isApplyStartClick: AvroOptional.boolean(options.isApplyStartClick),
      loggedIn: AvroOptional.boolean(isLoggedIn),
      platform: { RawClickPlatform: resolveRawClickPlatform(options.viewJobDisplay) },
      ctk: AvroOptional.string(options.ctk),
      accountKey: AvroOptional.string(options.accountId == null ? null : String(options.accountId)),
      grps: AvroOptional.string(resolveRawClickProctorGroups(options.grps)),
      dgToken: AvroOptional.string(options.dgToken),
      isCanary: AvroOptional.boolean(options.isCanary),
    });
  } catch {
    // Signals logging is best-effort and must not block click handling.
  }
}

export function createRawClickLoggingUrl(
  baseUrl: string,
  attrs: RawClickAttrs,
  isLoggedIn: boolean,
  options: RawClickOptions = {},
): string {
  const rawClickLoggingUrl = new URL("/m/rpc/rawclick", baseUrl);
  rawClickLoggingUrl.searchParams.set("tk", attrs.tk ?? "");
  rawClickLoggingUrl.searchParams.set("jobKey", attrs.jobKey ?? "");
  rawClickLoggingUrl.searchParams.set("surface", options.surface ?? "");
  rawClickLoggingUrl.searchParams.set("ia", options.ia ? "1" : "0");
  rawClickLoggingUrl.searchParams.set("isFirstAutoLoad", options.isFirstAutoLoad ? "1" : "0");
  rawClickLoggingUrl.searchParams.set("isApplyStartClick", options.isApplyStartClick ? "1" : "0");
  rawClickLoggingUrl.searchParams.set("loggedIn", isLoggedIn ? "1" : "0");

  return rawClickLoggingUrl.toString();
}
